import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { BottomSheet } from './BottomSheet';
import { ReauthFlow } from './ReauthFlow';
import { SourceSelect, buildReceiveSources } from './SourceSelect';
import { WarningIcon } from './icons';
import { useMe } from '../../hooks/useMe';
import { useWallet } from '../../hooks/useWallet';
import { cardErrorMessage, withdrawCard, type Card } from '../../lib/cards';
import { CARD_WITHDRAW_FEE_PCT, cardWithdrawPayoutUsd } from '@incpt/shared';

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

/** Withdraw from a card. Pulls funds off the card; the crypto payout to the
 *  user's wallet is settled by the treasury (admin/automated) step.
 *  Balance on the card face stays until Banana debit is confirmed — the top
 *  plaque covers that window (see usePendingWithdrawals). */
export function WithdrawSheet({
  card,
  balance,
  limits,
  open,
  onClose,
  onDone,
}: {
  card: Card | null;
  balance?: number;
  limits?: {
    minAmountUsd: number;
    maxAmountUsd: number;
    maxPerHour?: number;
    remainingThisHour?: number;
  } | null;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { me, initData, canFetch } = useMe();
  const { wallet, balances } = useWallet();
  const qc = useQueryClient();

  const [amount, setAmount] = useState('');
  const [destKey, setDestKey] = useState<string | null>(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Server-reported remainder after soft-reserve (from insufficient_card_balance). */
  const [serverAvailable, setServerAvailable] = useState<number | null>(null);

  useEffect(() => {
    if (open) return;
    setAmount('');
    setError(null);
    setServerAvailable(null);
    setBusy(false);
    setReauthOpen(false);
  }, [open]);

  // Where the withdrawn USDT lands — TRC-20 only (auto payout is USDT on Tron).
  const dests = buildReceiveSources(wallet, balances, t('nav.wallet')).filter((d) => d.chain === 'trx');
  const dest = dests.find((d) => d.key === destKey) ?? dests[0] ?? null;
  const chain = 'trx' as const;
  const payoutAddress = dest?.address ?? '';

  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;
  const amt = Number(amount) || 0;
  const payoutUsd = cardWithdrawPayoutUsd(amt);
  const feeUsd = Math.round((amt - payoutUsd) * 100) / 100;
  const min = limits?.minAmountUsd ?? 0;
  const maxAllowed = limits?.maxAmountUsd ?? Infinity;
  const available = serverAvailable ?? balance;
  const balanceMax = available ?? Infinity;
  const max = Math.min(balanceMax, maxAllowed);
  const belowMin = amt > 0 && amt < min;
  const aboveMax = amt > maxAllowed;
  const insufficient = amt > 0 && available != null && amt > available;
  const rateLimited = limits?.remainingThisHour != null && limits.remainingThisHour <= 0;
  const canWithdraw =
    amt >= min && amt > 0 && amt <= max && payoutUsd > 0 && !!payoutAddress && !insufficient && !rateLimited;

  const submit = async (token?: string) => {
    if (!card || !canFetch || !payoutAddress || !canWithdraw) return;
    setBusy(true);
    setError(null);
    setServerAvailable(null);
    try {
      await withdrawCard(
        initData,
        card.id,
        { amount: amt, address: payoutAddress, chain, token: 'USDT' },
        crypto.randomUUID(),
        token,
      );
      // Refresh intents so the top "Выводим…" plaque appears. Do NOT refetch
      // card balance here — face value stays until Banana debit is confirmed.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['card-intents', 'withdraw'] }),
        qc.invalidateQueries({ queryKey: ['cards'] }), // remainingThisHour
      ]);
      onDone();
    } catch (e) {
      const raw = String((e as Error)?.message ?? e);
      const avail = parseInsufficientAvailable(raw);
      if (avail != null) setServerAvailable(avail);
      setError(cardErrorMessage(raw, t, t('cards.withdrawFailed'), 'withdraw'));
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = () => {
    if (!canWithdraw) return;
    if (needsReauth) setReauthOpen(true);
    else void submit();
  };

  const onAmountChange = (raw: string) => {
    setAmount(raw.replace(/[^\d.]/g, ''));
    // Keep serverAvailable as a ceiling until the sheet closes — clearing it
    // on every keystroke re-enabled withdraw against a stale full balance.
    if (error) setError(null);
  };

  const blocked = busy || !canWithdraw;

  return (
    <>
      {/* Keep the sheet open during reauth so the bottom nav stays hidden. */}
      <BottomSheet open={open} onClose={onClose} title={t('cards.withdraw')}>
        {card && (
          <div className="mt-4 flex flex-col gap-3">
            <label className="flex items-center justify-between rounded-[16px] bg-[#f6f7f8] px-4 py-3">
              <span className="text-[14px] text-muted">{t('cards.amount')}</span>
              <div className="flex items-center">
                <input
                  value={amount}
                  onChange={(e) => onAmountChange(e.target.value)}
                  inputMode="decimal"
                  placeholder="0"
                  className="w-24 bg-transparent text-right text-[18px] font-medium text-ink outline-none placeholder:text-muted"
                />
                <span className="ml-0.5 text-[18px] font-medium text-ink">$</span>
              </div>
            </label>
            <p className="-mt-1 text-right text-[12px] text-muted">
              {[
                available != null ? t('cards.available', { amount: fmt(available) }) : null,
                limits ? t('cards.withdrawRange', { min: fmt(min), max: fmt(maxAllowed) }) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {belowMin && (
              <p className="-mt-2 text-right text-[12px] font-medium text-[#ff3b30]">
                {t('cards.errWithdrawMinAmount', { min: fmt(min) })}
              </p>
            )}
            {aboveMax && !belowMin && (
              <p className="-mt-2 text-right text-[12px] font-medium text-[#ff3b30]">
                {t('cards.errWithdrawMaxAmount', { max: fmt(maxAllowed) })}
              </p>
            )}
            {insufficient && !belowMin && !aboveMax && (
              <p className="-mt-2 text-right text-[12px] font-medium text-[#ff3b30]">
                {t('send.errInsufficient')}
              </p>
            )}
            {rateLimited && (
              <p className="flex items-center gap-1.5 text-[13px] font-medium text-[#ff3b30]">
                <WarningIcon size={15} />
                {t('cards.errWithdrawRate')}
              </p>
            )}

            <div>
              <span className="text-[13px] text-muted">{t('cards.receiveOn')}</span>
              <div className="mt-2">
                {dests.length === 0 ? (
                  <p className="text-[13px] text-muted">{t('cards.errWithdrawAddress')}</p>
                ) : (
                  <SourceSelect sources={dests} value={dest?.key ?? null} onChange={setDestKey} />
                )}
              </div>
            </div>

            <p className="rounded-[12px] bg-[#eef2ff] px-3 py-2.5 text-[13px] text-[#377dff]">
              {t('cards.withdrawNote')}
            </p>

            {amt > 0 && (
              <div className="flex flex-col gap-1.5 rounded-[16px] bg-[#f6f7f8] px-4 py-3 text-[13px]">
                <div className="flex justify-between">
                  <span className="text-muted">{t('cards.withdrawFee')} ({CARD_WITHDRAW_FEE_PCT}%)</span>
                  <span className="font-medium text-ink">${fmt(feeUsd)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">{t('cards.withdrawReceive')}</span>
                  <span className="font-medium text-ink">${fmt(payoutUsd)}</span>
                </div>
              </div>
            )}

            {error && !insufficient && (
              <p className="flex items-center gap-1.5 text-[13px] font-medium text-[#ff3b30]">
                <WarningIcon size={15} />
                {error}
              </p>
            )}

            <button
              onClick={onConfirm}
              disabled={blocked}
              className={`mt-1 flex h-14 items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white ${
                blocked ? 'pointer-events-none opacity-40' : 'active:opacity-90'
              }`}
            >
              {busy
                ? t('cards.paying')
                : `${t('cards.withdraw')} · $${fmt(payoutUsd > 0 ? payoutUsd : amt)}`}
            </button>
          </div>
        )}
      </BottomSheet>

      {reauthOpen && canFetch && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            onSuccess={(token) => {
              setReauthOpen(false);
              void submit(token);
            }}
            onCancel={() => setReauthOpen(false)}
          />
        </div>
      )}
    </>
  );
}

/** Pull `available` from API error JSON embedded in the thrown message. */
function parseInsufficientAvailable(raw: string): number | null {
  if (!raw.includes('insufficient_card_balance')) return null;
  const i = raw.indexOf('{');
  if (i < 0) return null;
  try {
    const body = JSON.parse(raw.slice(i)) as { available?: unknown };
    const n = Number(body.available);
    return Number.isFinite(n) && n >= 0 ? n : null;
  } catch {
    return null;
  }
}
